<template>
  <div class="lateral-menu">
    <!-- Menu Content - Always Open -->
    <div class="menu-content">
      <!-- Logo/Brand -->
      <div class="menu-header">
        <h3 class="brand-title">PRISM</h3>
        <p class="brand-subtitle">{{ currentContent.subtitle }}</p>
      </div>

      <!-- Navigation Links -->
      <nav class="menu-nav">
        <ul class="nav-list">
          <li class="nav-item">
            <router-link 
              to="/introduction" 
              class="nav-link"
              :class="{ 'active': $route.path === '/introduction' }"
            >
              <i class="nav-icon"></i>
              <span>{{ currentContent.introduction }}</span>
            </router-link>
          </li>
          <li class="nav-item">
            <router-link 
              to="/apt-map" 
              class="nav-link"
              :class="{ 'active': $route.path === '/apt-map' }"
            >
              <i class="nav-icon"></i>
              <span>{{ currentContent.aptMap }}</span>
            </router-link>
          </li>
          <li class="nav-item">
            <router-link 
              to="/cluster-profiles" 
              class="nav-link"
              :class="{ 'active': $route.path === '/cluster-profiles' }"
            >
              <i class="nav-icon"></i>
              <span>{{ currentContent.clusterProfiles }}</span>
            </router-link>
          </li>
          <li class="nav-item">
            <router-link 
              to="/base" 
              class="nav-link"
              :class="{ 'active': $route.path === '/base' }"
            >
              <i class="nav-icon"></i>
              <span>{{ currentContent.base }}</span>
            </router-link>
          </li>
        </ul>
      </nav>

      <!-- Footer -->
      <div class="menu-footer">
        <p class="footer-text">{{ currentContent.footer }}</p>
      </div>
    </div>

    <!-- Language Selector - Small corner -->
    <div class="language-selector-corner">
      <div class="btn-group" role="group">
        <button 
          @click="setLanguage('en')" 
          :class="['btn', 'btn-xs', language === 'en' ? 'btn-primary' : 'btn-outline-primary']"
          title="English"
        >
          EN
        </button>
        <button 
          @click="setLanguage('es')" 
          :class="['btn', 'btn-xs', language === 'es' ? 'btn-primary' : 'btn-outline-primary']"
          title="Español"
        >
          ES
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { languageStore } from '../stores/languageStore'

export default {
  name: 'LateralMenu',
  data() {
    return {
      content: {
        en: {
          subtitle: 'Predictive Risk Identification & Strategy Model',
          introduction: 'Introduction',
          aptMap: 'APT Map',
          clusterProfiles: 'Cluster Profiles',
          base: 'Base',
          footer: '© 2025 BASE4 Security'
        },
        es: {
          subtitle: 'Modelo de Identificación de Riesgo Predictivo y Estrategia',
          introduction: 'Introducción',
          aptMap: 'Mapa APT',
          clusterProfiles: 'Perfiles de Clusters',
          base: 'Base',
          footer: '© 2025 BASE4 Security'
        }
      }
    }
  },
  computed: {
    language() {
      return languageStore.getLanguage()
    },
    currentContent() {
      return this.content[this.language]
    }
  },
  methods: {
    setLanguage(lang) {
      languageStore.setLanguage(lang)
    }
  }
}
</script>

<style scoped>
.lateral-menu {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1000;
  height: 100vh;
}

.menu-content {
  position: fixed;
  top: 0;
  left: 0;
  width: 300px;
  height: 100vh;
  background: var(--mm-custom-color-3);
  color: var(--mm-custom-color-5);
  padding: 20px;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  box-shadow: 2px 0 10px rgba(0, 0, 0, 0.5);
  border-right: 2px solid var(--mm-custom-color-2);
}

.menu-header {
  text-align: center;
  margin-bottom: 30px;
  padding-bottom: 20px;
  border-bottom: 1px solid var(--mm-custom-color-2);
}

.brand-title {
  font-size: 2rem;
  font-weight: bold;
  margin: 0;
  color: var(--mm-custom-color-2);
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.5);
}

.brand-subtitle {
  font-size: 0.9rem;
  margin: 5px 0 0 0;
  opacity: 0.9;
  line-height: 1.4;
  color: var(--mm-custom-color-5);
}

.menu-nav {
  flex: 1;
}

.nav-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.nav-item {
  margin-bottom: 10px;
}

.nav-link {
  display: flex;
  align-items: center;
  padding: 15px 20px;
  color: var(--mm-custom-color-5);
  text-decoration: none;
  border-radius: 10px;
  transition: all 0.3s ease;
  background: rgba(220, 120, 15, 0.1);
  border: 1px solid transparent;
}

.nav-link:hover {
  background: rgba(220, 120, 15, 0.2);
  transform: translateX(5px);
  color: var(--mm-custom-color-5);
  text-decoration: none;
  border-color: var(--mm-custom-color-2);
}

.nav-link.active {
  background: var(--mm-custom-color-2);
  box-shadow: 0 4px 15px rgba(220, 120, 15, 0.3);
  color: var(--mm-custom-color-5);
}

.nav-icon {
  font-size: 1.2rem;
  margin-right: 15px;
  width: 20px;
  text-align: center;
}

.menu-footer {
  text-align: center;
  padding-top: 20px;
  border-top: 1px solid var(--mm-custom-color-2);
  margin-top: auto;
}

.footer-text {
  font-size: 0.8rem;
  opacity: 0.7;
  margin: 0;
  color: var(--mm-custom-color-5);
}

/* Language Selector Corner */
.language-selector-corner {
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 1002;
}

.language-selector-corner .btn-group {
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.3);
}

.language-selector-corner .btn {
  border: none;
  padding: 4px 8px;
  font-size: 0.7rem;
  font-weight: bold;
  min-width: 35px;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .menu-content {
    width: 280px;
  }
  
  .language-selector-corner {
    top: 15px;
    right: 15px;
  }
}
</style>
